import { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquareHeart, ChevronLeft, ChevronRight } from 'lucide-react';
import { TestimonialCard } from '../TestimonialCard/TestimonialCard';
import { useApprovedReviews } from '../../services/reviewService';

/**
 * Recent Reviews section — Grid/Masonry layout with numbered pagination.
 */
export function Testimonials() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useApprovedReviews(page);

  const reviews = data?.reviews || [];
  const totalPages = data?.totalPages || 1;

  if (isLoading && page === 1) {
    return (
      <section className="py-10 sm:py-14 bg-brand-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="bg-brand-white rounded-lg border border-ui-border p-5 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-light" />
                  <div className="space-y-2 flex-1">
                    <div className="h-4 bg-brand-light rounded w-28" />
                    <div className="h-3 bg-brand-light rounded w-32" />
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  <div className="h-3 bg-brand-light rounded w-full" />
                  <div className="h-3 bg-brand-light rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error || !reviews.length) return null;

  // Build page number array
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }
  // Show max 5 page buttons with ellipsis
  const visiblePages = getVisiblePages(page, totalPages);

  return (
    <section className="py-10 sm:py-14 bg-brand-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="flex items-center gap-2 mb-6"
        >
          <MessageSquareHeart size={26} className="text-brand-red" />
          <h2 className="text-xl sm:text-2xl font-extrabold text-brand-dark">
            Recent Reviews
          </h2>
          {data?.totalCount > 0 && (
            <span className="text-sm text-brand-gray ml-1">({data.totalCount})</span>
          )}
        </motion.div>

        {/* Grid / Masonry layout */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          {reviews.map((testimonial, index) => (
            <div key={testimonial.id} className="break-inside-avoid">
              <TestimonialCard
                testimonial={testimonial}
                index={index}
              />
            </div>
          ))}
        </div>

        {/* Numbered Pagination */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-start gap-1.5 mt-8"
          >
            {/* Prev */}
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-ui-border bg-brand-white hover:bg-brand-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} className="text-brand-dark" />
            </button>

            {/* Page numbers */}
            {visiblePages.map((p, i) =>
              p === '...' ? (
                <span key={`dot-${i}`} className="w-9 h-9 flex items-center justify-center text-sm text-brand-gray">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={
                    p === page
                      ? 'w-9 h-9 flex items-center justify-center rounded-lg text-sm font-bold bg-brand-red text-brand-white shadow-sm'
                      : 'w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-brand-dark border border-ui-border bg-brand-white hover:bg-brand-light transition-colors'
                  }
                >
                  {p}
                </button>
              )
            )}

            {/* Next */}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-ui-border bg-brand-white hover:bg-brand-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight size={16} className="text-brand-dark" />
            </button>
          </motion.div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <div className="w-6 h-6 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Helper — returns an array of page numbers with '...' for display.
 * Shows first page, last page, current, and 1 neighbor on each side.
 */
function getVisiblePages(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = [];
  pages.push(1);

  if (current > 3) pages.push('...');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push('...');

  pages.push(total);
  return pages;
}
